import React, { forwardRef, useState, useEffect } from 'react'
import { styled } from '@mui/material/styles'
import { Box, IconButton } from '@mui/material'
import InputField from './InputField'
import type { TextFieldProps } from '@mui/material/TextField'

interface FileInputProps extends Omit<TextFieldProps, 'type' | 'onChange'> {
  name: string
  label?: string
  placeholder?: string
  required?: boolean
  accept?: string
  multiple?: boolean
  onChange?: (files: FileList | null) => void
  onBlur?: () => void
  onClear?: () => void
  value?: FileList | null
  error?: boolean
  helperText?: string
}

const FileInputContainer = styled(Box)({
  position: 'relative',
  width: '100%'
})

const FileInputStyled = styled(InputField)(({}) => ({
  '& .MuiInputBase-root': {
    position: 'relative',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.02)'
    }
  },
  '& .MuiInputBase-input': {
    cursor: 'pointer',
    paddingRight: '48px !important'
  }
}))

const UploadIcon = styled('i')({
  position: 'absolute',
  right: '16px',
  top: '50%',
  transform: 'translateY(-50%)',
  fontSize: '20px',
  color: 'var(--mui-palette-text-primary)',
  pointerEvents: 'none',
  zIndex: 1
})

const HiddenFileInput = styled('input')({
  position: 'absolute',
  opacity: 0,
  width: '100%',
  height: '100%',
  cursor: 'pointer',
  top: 0,
  left: 0,
  zIndex: 1,
  '&::-webkit-file-upload-button': {
    display: 'none'
  }
})

const ClearButtonContainer = styled(Box)({
  position: 'relative',
  zIndex: 3
})

export const FileInput = forwardRef<HTMLInputElement, FileInputProps>(
  (
    {
      name,
      label,
      placeholder = 'Import File',
      required = false,
      accept,
      multiple = false,
      onChange,
      onBlur,
      onClear,
      value,
      error = false,
      helperText,
      ...props
    },
    ref
  ) => {
    const [selectedFiles, setSelectedFiles] = useState<FileList | null>(value || null)
    const [inputKey, setInputKey] = useState(0) // Thêm key để force re-render input

    // Sync với value từ react-hook-form
    useEffect(() => {
      setSelectedFiles(value || null)
      // If value becomes null, increment inputKey to reset the input

      if (!value) {
        setInputKey(prev => prev + 1)
      }
    }, [value])

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files

      setSelectedFiles(files)
      onChange?.(files)
      onBlur?.()
    }

    const handleClear = () => {
      setSelectedFiles(null)
      onChange?.(null)
      onClear?.()
      setInputKey(prev => prev + 1)
    }

    const getDisplayText = () => {
      if (selectedFiles && selectedFiles.length > 0) {
        if (multiple) {
          return `${selectedFiles.length} file(s) selected`
        } else {
          return selectedFiles[0].name
        }
      }

      return placeholder
    }

    const hasFile = selectedFiles && selectedFiles.length > 0

    return (
      <FileInputContainer>
        <FileInputStyled
          {...props}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          value={getDisplayText()}
          slotProps={{
            input: {
              readOnly: true,
              style: hasFile ? { color: '#00BAD1' } : { color: 'rgba(0, 0, 0, 0.38)' },
              endAdornment: (
                <>
                  {selectedFiles && selectedFiles.length > 0 && onClear && (
                    <ClearButtonContainer className='mr-6'>
                      <IconButton size='small' onClick={handleClear} aria-label='clear file selection' tabIndex={-1}>
                        <i className='tabler-circle-x text-[16px] text-textPrimary'></i>
                      </IconButton>
                    </ClearButtonContainer>
                  )}
                  <UploadIcon className='tabler-upload' />
                  <HiddenFileInput
                    key={inputKey}
                    ref={ref}
                    type='file'
                    name={name}
                    accept={accept}
                    multiple={multiple}
                    onChange={handleFileChange}
                  />
                </>
              )
            }
          }}
        />
      </FileInputContainer>
    )
  }
)

FileInput.displayName = 'FileInput'

export default FileInput
